import { ReactNode, memo, useMemo } from 'react';
import { Box } from '@mui/material';

type AnimatedContentSwitchProps<TModeKey extends string> = {
  /**
   * The key of the content variant to display.
   */
  mode: TModeKey;
  /**
   * A Record (object) where keys are of type TModeKey
   * and values are the ReactNode content for that mode.
   */
  contentVariants: Record<TModeKey, ReactNode>;
  /**
   * An array of TModeKey values defining the intended order of the sidebar contents.
   */
  orderedModes: TModeKey[];
  animationDuration?: number;
};

// Memoized content box to prevent unnecessary re-renders
const ContentBox = memo(({ content }: { content: ReactNode }) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      flexShrink: 0,
      flexGrow: 1,
      overflow: 'hidden',
      // overflowY: 'auto',
      // overflowX: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      /* IE and Edge */
      msOverflowStyle: 'none',
      /* Firefox */
      scrollbarWidth: 'none',
      willChange: 'transform', // Optimize for animations
    }}
  >
    {content}
  </Box>
));

ContentBox.displayName = 'ContentBox';

export const AnimatedContentSwitch = <TModeKey extends string>({ mode, contentVariants, orderedModes, animationDuration = 300 }: AnimatedContentSwitchProps<TModeKey>) => {
  // Calculate transform only when mode changes
  const xTranslate = useMemo(() => {
    const contentIndex = orderedModes.indexOf(mode);
    return contentIndex * -100;
  }, [mode, orderedModes]);

  // Memoize content array to prevent unnecessary re-renders
  const contentArray = useMemo(() => {
    return Object.values(contentVariants).map((content, index) => <ContentBox key={index} content={content as ReactNode} />);
  }, [contentVariants]);

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          whiteSpace: 'nowrap',
          width: '100%',
          height: '100%',
          transform: `translateX(${xTranslate}%)`,
          transition: `transform ${animationDuration}ms ease`,
          willChange: 'transform', // Optimize for animations
        }}
      >
        {contentArray}
      </Box>
    </Box>
  );
};
